import { styled, Skeleton, SxProps, Typography } from '@mui/material'
import { UserAvatar } from '$common/avatar'
import { AvatarSize } from '$common/avatar/avatar-base/types'
import { AVATAR_SIZES } from '$common/avatar/avatar-base/conf'
import { toLocale } from '$func/text'

interface Props {
  userIds: string[]
  size?: AvatarSize
  className?: string
  sx?: SxProps
  onClick?: () => unknown
  showOthersCount?: boolean
}

const MAX_AVATARS_TO_SHOW = 3

export const AvatarGroup = ({
  userIds,
  size = AvatarSize._3XS,
  className,
  sx,
  onClick,
  showOthersCount,
}: Props) => {
  const count = userIds.length
  return (
    <Root
      className={className}
      sx={{
        cursor: onClick ? 'pointer' : undefined,
        ...sx,
        '--margin-inline-end': `-${AVATAR_SIZES[size] / 2}px`,
      }}
      onClick={onClick}
    >
      {userIds ? (
        <AvatarWrapper>
          {userIds
            .slice(0, Math.min(count, MAX_AVATARS_TO_SHOW))
            .reverse()
            .map(id => {
              return <StyledUserAvatar key={id} userId={id} size={size} />
            })}

          {showOthersCount && count > MAX_AVATARS_TO_SHOW && (
            <OthersCountWrapper
              sx={{
                width: `${AVATAR_SIZES[size]}px`,
                height: `${AVATAR_SIZES[size]}px`,
              }}
            >
              <Typography dir="ltr" variant="caption2" color={'inherit'}>
                +{toLocale(Math.min(count - MAX_AVATARS_TO_SHOW, 99), false)}
              </Typography>
            </OthersCountWrapper>
          )}
        </AvatarWrapper>
      ) : (
        <div style={{ display: 'flex' }}>
          <FormattedSkeleton
            animation={false}
            width={`${AVATAR_SIZES[size]}px`}
            height={`${AVATAR_SIZES[size]}px`}
            variant="circular"
            className={'FORMATTED'}
          />
          <FormattedSkeleton
            animation={false}
            variant="circular"
            width={`${AVATAR_SIZES[size]}px`}
            height={`${AVATAR_SIZES[size]}px`}
          />
          <FormattedSkeleton
            animation={false}
            variant="circular"
            width={`${AVATAR_SIZES[size]}px`}
            height={`${AVATAR_SIZES[size]}px`}
          />
        </div>
      )}
    </Root>
  )
}

const FormattedSkeleton = styled(Skeleton)`
  border: 1px solid #ffffff;
  margin: 0 -5px;
  box-sizing: border-box;
  &:not(:first-child) {
    margin-inline-end: var(--margin-inline-end);
  }
`

const OthersCountWrapper = styled('div')`
  border-radius: 50%;
  box-sizing: border-box;
  background: linear-gradient(180deg, #8cbfd9 0.01%, #4095bf 100%);
  color: ${({ theme }) => theme.palette.neutral000};
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;

  &:not(:first-child) {
    margin-inline-end: var(--margin-inline-end);
  }
`

const AvatarWrapper = styled('div')`
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  .MuiBadge-root:not(:first-child) .avatar-base__root {
    margin-inline-end: var(--margin-inline-end);
  }
  .MuiSkeleton-root:not(:first-child) {
    margin-inline-end: var(--margin-inline-end);
  }
`

const Root = styled('button')`
  height: 22px;
  width: fit-content;
  background: transparent;
  border: none;
  outline: none;
  padding: unset;
`

const StyledUserAvatar = styled(UserAvatar)`
  outline: 1px solid ${({ theme }) => theme.palette.neutral000};
`
